import { defineTokens } from "@chakra-ui/react";

export const breakpoints = {
  sm: { value: "20em" },
  md: { value: "26em" },
  lg: { value: "48em" },
  xl: { value: "80em" },
  "2xl": { value: "120em" }
};

export const fontSizes = {
  xs: { value: "0.75rem" },
  sm: { value: "0.875rem" },
  md: { value: "1rem" },
  lg: { value: "1.13rem" },
  xl: { value: "1.25rem" },
  "2xl": { value: "1.5rem" },
  "3xl": { value: "1.75rem" },
  "4xl": { value: "2rem" },
  "5xl": { value: "2.5rem" },
  body: { value: "1rem" },
  heading: { value: "1rem" },
  sectionTitle: { value: "1rem" }
};

export const fontWeights = {
  hairline: { value: "100" },
  thin: { value: "200" },
  light: { value: "300" },
  normal: { value: "400" },
  medium: { value: "500" },
  semibold: { value: "600" },
  bold: { value: "700" },
  extrabold: { value: "800" },
  black: { value: "900" },
  body: { value: "400" },
  heading: { value: "700" }
};

export const containerSizes = {
  sm: { value: "640px" },
  md: { value: "768px" },
  lg: { value: "1024px" },
  xl: { value: "1280px" }
};

export const lineHeights = {
  normal: { value: "normal" },
  none: { value: "1" },
  shorter: { value: "1.25" },
  short: { value: "1.375" },
  base: { value: "1.5" },
  tall: { value: "1.625" },
  taller: { value: "2" },
  "3": { value: "1.28rem" },
  "4": { value: "1.30rem" },
  "5": { value: "1.33rem" },
  "6": { value: "1.36rem" },
  "7": { value: "1.52rem" },
  "8": { value: "1.58rem" },
  "9": { value: "1.61rem" },
  "10": { value: "2.5rem" }
};

export const colors = {
  brand: {
    50: { value: "#BEE3F8" },
    100: { value: "#90CDF4" },
    200: { value: "#63B3ED" },
    300: { value: "#4299E1" },
    400: { value: "#3182CE" },
    500: { value: "#2B6CB0" },
    600: { value: "#2C5282" },
    700: { value: "#2A4365" },
    800: { value: "#1A365D" },
    900: { value: "#131D47" }
  },
  emoji: {
    500: { value: "#FFCE00" }
  },
  x: {
    500: { value: "#000000" },
    600: { value: "#333333" }
  },
  /** v2 rendered social buttons via Chakra's built-in `facebook` colorScheme. */
  facebook: {
    50: { value: "#E8F4F9" },
    100: { value: "#D9DEE9" },
    200: { value: "#B7C2DA" },
    300: { value: "#6482C0" },
    400: { value: "#4267B2" },
    500: { value: "#385898" },
    600: { value: "#314E89" },
    700: { value: "#29487D" },
    800: { value: "#223B67" },
    900: { value: "#1E355B" }
  },
  linkedin: {
    50: { value: "#E8F3FC" },
    100: { value: "#CDE3F7" },
    200: { value: "#9BC7EF" },
    300: { value: "#69ABE7" },
    400: { value: "#378FDF" },
    500: { value: "#0A66C2" },
    600: { value: "#004182" },
    700: { value: "#003466" },
    800: { value: "#00274D" },
    900: { value: "#001A33" }
  },
  feedback: {
    info: { value: "#1765C4" },
    success: { value: "#348632" },
    warning: { value: "#C55200" },
    error: { value: "#E2221D" }
  },
  /** v2 Chakra hex scales — v3 defaults are oklch-derived and differ. */
  gray: {
    50: { value: "#F7FAFC" },
    100: { value: "#EDF2F7" },
    200: { value: "#E2E8F0" },
    300: { value: "#CBD5E0" },
    400: { value: "#A0AEC0" },
    500: { value: "#718096" },
    600: { value: "#4A5568" },
    700: { value: "#2D3748" },
    800: { value: "#1A202C" },
    900: { value: "#171923" }
  },
  blue: {
    50: { value: "#EBF8FF" },
    100: { value: "#BEE3F8" },
    200: { value: "#90CDF4" },
    300: { value: "#63B3ED" },
    400: { value: "#4299E1" },
    500: { value: "#3182CE" },
    600: { value: "#2B6CB0" },
    700: { value: "#2C5282" },
    800: { value: "#2A4365" },
    900: { value: "#1A365D" }
  },
  red: {
    50: { value: "#FFF5F5" },
    100: { value: "#FED7D7" },
    200: { value: "#FEB2B2" },
    300: { value: "#FC8181" },
    400: { value: "#F56565" },
    500: { value: "#E53E3E" },
    600: { value: "#C53030" },
    700: { value: "#9B2C2C" },
    800: { value: "#822727" },
    900: { value: "#63171B" }
  },
  green: {
    50: { value: "#F0FFF4" },
    100: { value: "#C6F6D5" },
    200: { value: "#9AE6B4" },
    300: { value: "#68D391" },
    400: { value: "#48BB78" },
    500: { value: "#38A169" },
    600: { value: "#2F855A" },
    700: { value: "#276749" },
    800: { value: "#22543D" },
    900: { value: "#1C4532" }
  },
  orange: {
    50: { value: "#FFFAF0" },
    100: { value: "#FEEBC8" },
    200: { value: "#FBD38D" },
    300: { value: "#F6AD55" },
    400: { value: "#ED8936" },
    500: { value: "#DD6B20" },
    600: { value: "#C05621" },
    700: { value: "#9C4221" },
    800: { value: "#7B341E" },
    900: { value: "#652B19" }
  },
  yellow: {
    50: { value: "#FFFFF0" },
    100: { value: "#FEFCBF" },
    200: { value: "#FAF089" },
    300: { value: "#F6E05E" },
    400: { value: "#ECC94B" },
    500: { value: "#D69E2E" },
    600: { value: "#B7791F" },
    700: { value: "#975A16" },
    800: { value: "#744210" },
    900: { value: "#5F370E" }
  },
  purple: {
    50: { value: "#FAF5FF" },
    100: { value: "#E9D8FD" },
    200: { value: "#D6BCFA" },
    300: { value: "#B794F4" },
    400: { value: "#9F7AEA" },
    500: { value: "#805AD5" },
    600: { value: "#6B46C1" },
    700: { value: "#553C9A" },
    800: { value: "#44337A" },
    900: { value: "#322659" }
  },
  pink: {
    50: { value: "#FFF5F7" },
    100: { value: "#FED7E2" },
    200: { value: "#FBB6CE" },
    300: { value: "#F687B3" },
    400: { value: "#ED64A6" },
    500: { value: "#D53F8C" },
    600: { value: "#B83280" },
    700: { value: "#97266D" },
    800: { value: "#702459" },
    900: { value: "#521B41" }
  },
  teal: {
    50: { value: "#E6FFFA" },
    100: { value: "#B2F5EA" },
    200: { value: "#81E6D9" },
    300: { value: "#4FD1C5" },
    400: { value: "#38B2AC" },
    500: { value: "#319795" },
    600: { value: "#2C7A7B" },
    700: { value: "#285E61" },
    800: { value: "#234E52" },
    900: { value: "#1D4044" }
  },
  cyan: {
    50: { value: "#EDFDFD" },
    100: { value: "#C4F1F9" },
    200: { value: "#9DECF9" },
    300: { value: "#76E4F7" },
    400: { value: "#0BC5EA" },
    500: { value: "#00B5D8" },
    600: { value: "#00A3C4" },
    700: { value: "#0987A0" },
    800: { value: "#086F83" },
    900: { value: "#065666" }
  }
};

export const shadows = {
  /** v2 Chakra `shadows.outline` — focus ring used by links, menu, emoji-radio, etc. */
  outline: {
    value: "0 0 0 3px rgba(66, 153, 225, 0.6)"
  },
  topLg: {
    value:
      "0 -10px 15px -3px rgba(0, 0, 0, 0.1), 0 -4px 6px -2px rgba(0, 0, 0, 0.05)"
  },
  bottomLg: {
    value:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
  }
};

export const radii = {
  site: { value: "0.25rem" }
};

export const fonts = {
  body: {
    value:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  },
  heading: {
    value:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  }
};

export const tokens = defineTokens({
  breakpoints,
  fontSizes,
  fontWeights,
  lineHeights,
  colors,
  shadows,
  radii,
  fonts,
  sizes: {
    container: containerSizes
  }
});

export type EhqThemeOverrides = {
  colors?: {
    brand?: Record<string, string>;
    [key: string]: unknown;
  };
  fonts?: Record<string, string>;
  fontSizes?: Record<string, string>;
  fontWeights?: Record<string, string | number>;
  radii?: Record<string, string>;
  shadows?: Record<string, string>;
};

export function mergeThemeTokens(overrides: EhqThemeOverrides = {}) {
  const nextColors = { ...colors };

  if (overrides.colors?.brand) {
    nextColors.brand = {
      ...nextColors.brand,
      ...Object.fromEntries(
        Object.entries(overrides.colors.brand).map(([key, value]) => [
          key,
          { value }
        ])
      )
    };
  }

  return defineTokens({
    breakpoints,
    fontSizes: {
      ...fontSizes,
      ...Object.fromEntries(
        Object.entries(overrides.fontSizes ?? {}).map(([key, value]) => [
          key,
          { value }
        ])
      )
    },
    fontWeights: {
      ...fontWeights,
      ...Object.fromEntries(
        Object.entries(overrides.fontWeights ?? {}).map(([key, value]) => [
          key,
          { value: String(value) }
        ])
      )
    },
    lineHeights,
    colors: nextColors,
    shadows: {
      ...shadows,
      ...Object.fromEntries(
        Object.entries(overrides.shadows ?? {}).map(([key, value]) => [
          key,
          { value }
        ])
      )
    },
    radii: {
      ...radii,
      ...Object.fromEntries(
        Object.entries(overrides.radii ?? {}).map(([key, value]) => [
          key,
          { value }
        ])
      )
    },
    fonts: {
      ...fonts,
      ...Object.fromEntries(
        Object.entries(overrides.fonts ?? {}).map(([key, value]) => [
          key,
          { value }
        ])
      )
    },
    sizes: {
      container: containerSizes
    }
  });
}
